import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CountBadge } from '../../../web/components-v2/CountBadge';
import { AlarmClockIcon } from '../../../web/components/svgs/icons/AlarmClockIcon';
import { ApproveMarkIcon } from '../../../web/components/svgs/icons/ApproveMarkIcon';
import { CalendarDateIcon } from '../../../web/components/svgs/icons/CalendarDateIcon';
import { CallLogIcon } from '../../../web/components/svgs/icons/CallLogIcon';
import { FollowUpsIcon } from '../../../web/components/svgs/icons/FollowUpsIcon';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { SearchIcon } from '../../../web/components/svgs/icons/SearchIcon';
import { SettingsIcon } from '../../../web/components/svgs/icons/SettingsIcon';
import { brandPrimaryLight } from '../../styles/colors';
import { TransparentButton } from '../TransparentButton';
import { TrophyIcon } from '../svgs/icons/TrophyIcon';
import { styleSheet } from './styles';

export enum NavLinkName {
	CallLog = 'CallLog',
	Demos = 'Demos',
	FollowUps = 'FollowUps',
	HoursReview = 'HoursReview',
	Leaderboard = 'Leaderboard',
	Queue = 'Queue',
	Search = 'Search',
	Settings = 'Settings',
	Timesheet = 'Timesheet',
}

export interface INavLink {
	badge?: number;
	label?: string;
	onClick?: () => void;
	path?: string;
	text: NavLinkName;
}

interface IProps {
	className?: string;
	link: INavLink;
}

const activeColor = '#fff';
const inactiveColor = brandPrimaryLight;

export const NavLink: React.FC<IProps> = ({ className = '', link }) => {
	const location = useLocation();

	const [isActive, setIsActive] = useState(location.pathname.includes(link.path));

	useEffect(() => {
		setIsActive(location.pathname.includes(link.path));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	const onMouseEnter = () => {
		setIsActive(true);
	};

	const onMouseLeave = () => {
		setIsActive(location.pathname.includes(link.path));
	};

	const renderIcon = () => {
		switch (link.text) {
			case NavLinkName.CallLog:
				return <CallLogIcon fill={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.Demos:
				return <CalendarDateIcon fillColor={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.FollowUps:
				return <FollowUpsIcon fill={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.Leaderboard:
				return <TrophyIcon fillColor={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.Queue:
				return <PhoneIcon fill={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.Search:
				return <SearchIcon fillColor={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.Settings:
				return <SettingsIcon stroke={isActive ? activeColor : inactiveColor} />;
			case NavLinkName.Timesheet:
				return <AlarmClockIcon color={isActive ? activeColor : inactiveColor} height={20} width={20} />;
			case NavLinkName.HoursReview:
				return <ApproveMarkIcon color={isActive ? activeColor : inactiveColor} height={18} width={19} />;
			default:
				return null;
		}
	};

	return link.onClick ? (
		<TransparentButton
			className={`${css(styleSheet.navLinkContainer, isActive && styleSheet.active)} ${className}`}
			onClick={link.onClick}
		>
			{renderIcon()}
		</TransparentButton>
	) : (
		<Link
			className={`${css(styleSheet.navLinkContainer, isActive && styleSheet.active, !!link.badge && styleSheet.withBadge)} ${className}`}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			aria-label={link.label}
			to={link.path}
		>
			{renderIcon()}
			{link.badge ? <CountBadge count={link.badge} aria-hidden /> : null}
		</Link>
	);
};
